import React, { useState, useEffect, useCallback, useRef } from 'react';
import { addDays, startOfWeek, format, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { uk } from 'date-fns/locale';
import { AxiosInstance } from '../baseUrl';
import Swal from 'sweetalert2';
import EventBlock from './EventBlock';
import CreateEventModal from './CreateEventModal';
import EventDetailModal from './EventDetailModal';
import DoneEventModal from './DoneEventModal';
import { PulseLoader } from 'react-spinners';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';

// Send local time to backend so it's stored and displayed correctly (no UTC offset shift)
const localISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` +
    `T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:00`;

const HOUR_HEIGHT = 64;
const START_HOUR  = 7;
const END_HOUR    = 24;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const SNAP_MIN    = 15;
const GUTTER_W    = 48;

const snap  = (m) => Math.round(m / SNAP_MIN) * SNAP_MIN;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const COLOR_BG = { green: '#33B679', blue: 'rgba(20,20,80,0.9)', purple: '#9b59b6' };
const COLOR_BORDER = { blue: '2px solid #4a90d9' };

export default function CalendarView() {
    const [weekStart,   setWeekStart]   = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [events,      setEvents]      = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [showCreate,  setShowCreate]  = useState(false);
    const [createSlot,  setCreateSlot]  = useState(null);
    const [detailEvent, setDetailEvent] = useState(null);
    const [doneEvent,   setDoneEvent]   = useState(null);
    const [activeEv,    setActiveEv]    = useState(null);   // event being dragged (for overlay)
    const [dragTime,    setDragTime]    = useState('');

    const gridRef     = useRef(null);
    const justDragged = useRef(false);

    const days    = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const weekEnd = addDays(weekStart, 7);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    const load = useCallback(async () => {
        setLoading(true);
        // scroll to 8 AM so early-morning events are visible
        if (gridRef.current) gridRef.current.scrollTop = (13 - START_HOUR) * HOUR_HEIGHT;
        try {
            const res = await AxiosInstance.get('/events', {
                params: {
                    week_start: format(weekStart, "yyyy-MM-dd'T'HH:mm:ss"),
                    week_end:   format(weekEnd,   "yyyy-MM-dd'T'HH:mm:ss"),
                },
            });
            setEvents(res.data);
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', background: '#1a1a1a', color: '#e8e8e8' });
        } finally {
            setLoading(false);
        }
    }, [weekStart]);

    useEffect(() => { load(); }, [load]);

    const handleDragStart = ({ active }) => {
        setActiveEv(active.data.current?.event ?? null);
        setDragTime('');
    };

    const handleDragMove = ({ active, delta }) => {
        if (!active?.data?.current) return;
        const { originalTopPx } = active.data.current;
        const newTopPx = originalTopPx + delta.y;
        const rawMins  = (newTopPx / HOUR_HEIGHT + START_HOUR) * 60;
        const newMins  = clamp(snap(rawMins), START_HOUR * 60, (END_HOUR - 1) * 60);
        setDragTime(`${String(Math.floor(newMins / 60)).padStart(2, '0')}:${String(newMins % 60).padStart(2, '0')}`);
    };

    const handleDragEnd = async ({ active, delta }) => {
        setActiveEv(null);
        justDragged.current = true;
        setTimeout(() => { justDragged.current = false; }, 300);

        if (!active.data.current) return;
        const { event, originalDayIdx, originalTopPx } = active.data.current;

        if (event.is_done) return;

        // column: original idx + how many columns the pointer moved horizontally
        const gr   = gridRef.current?.getBoundingClientRect();
        const colW = gr ? (gr.width - GUTTER_W) / 7 : 0;
        const newDayIdx = clamp(
            originalDayIdx + (colW > 0 ? Math.round(delta.x / colW) : 0),
            0, 6
        );

        // time: original pixel top + vertical delta → new time
        const newTopPx = originalTopPx + delta.y;
        const rawMins  = (newTopPx / HOUR_HEIGHT + START_HOUR) * 60;
        const newMins  = clamp(snap(rawMins), START_HOUR * 60, (END_HOUR - 1) * 60);

        const newDay   = days[newDayIdx];
        const newStart = new Date(newDay);
        newStart.setHours(Math.floor(newMins / 60), newMins % 60, 0, 0);
        const dur    = new Date(event.end_time) - new Date(event.start_time);
        const newEnd = new Date(newStart.getTime() + dur);

        // no-op if same slot
        if (localISO(newStart) === event.start_time.slice(0, 16)) return;

        let applyToFollowing = false;
        if (event.recurrence_rule) {
            const { isDenied } = await Swal.fire({
                title: 'Перенести подію',
                showDenyButton: true,
                confirmButtonText: 'Тільки цей',
                denyButtonText: 'Всі наступні',
                background: '#1a1a1a', color: '#e8e8e8',
            });
            applyToFollowing = isDenied;
        }

        try {
            await AxiosInstance.post(`/events/${event.id}/move`, {
                start_time:         localISO(newStart),
                end_time:           localISO(newEnd),
                apply_to_following: applyToFollowing,
            });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Помилка', text: 'Не вдалося перенести', background: '#1a1a1a', color: '#e8e8e8' });
        }
    };

    const timeToY   = (s)  => { const d = new Date(s); return (d.getHours() - START_HOUR + d.getMinutes() / 60) * HOUR_HEIGHT; };
    const durationH = (ev) => Math.max((new Date(ev.end_time) - new Date(ev.start_time)) / 3600000 * HOUR_HEIGHT, 24);

    const handleColClick = (e, day) => {
        if (justDragged.current) return;
        const rect    = e.currentTarget.getBoundingClientRect();
        const grid    = gridRef.current;
        const scrollY = grid ? grid.scrollTop : 0;
        const yInContent = e.clientY - rect.top + scrollY;
        const snapped = clamp(snap((yInContent / HOUR_HEIGHT + START_HOUR) * 60), START_HOUR * 60, (END_HOUR - 1) * 60);
        const start   = new Date(day);
        start.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
        setCreateSlot({ start, end: new Date(start.getTime() + 3600000) });
        setShowCreate(true);
    };

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
            <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#0f0f0f' }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #2e2e2e', gap: 10, background: '#1a1a1a' }}>
                    <button onClick={() => setWeekStart(w => subWeeks(w, 1))} style={navBtn}>←</button>
                    <span style={{ color: '#e8e8e8', fontWeight: 600, minWidth: 200, textAlign: 'center', fontSize: 14 }}>
                        {format(weekStart, 'd MMM', { locale: uk })} – {format(addDays(weekStart, 6), 'd MMM yyyy', { locale: uk })}
                    </span>
                    <button onClick={() => setWeekStart(w => addWeeks(w, 1))} style={navBtn}>→</button>
                    <button onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} style={{ ...navBtn, marginLeft: 4 }}>Сьогодні</button>
                    <div style={{ flex: 1 }} />
                    <button onClick={() => { setCreateSlot(null); setShowCreate(true); }} style={{ ...navBtn, background: '#5865f2', color: '#fff', border: 'none', padding: '6px 18px' }}>
                        + Подія
                    </button>
                </div>

                {/* Hidden events warning (before 7:00) */}
                {events.filter(ev => new Date(ev.start_time).getHours() < START_HOUR).map(ev => (
                    <div key={ev.id}
                         onClick={() => setDetailEvent(ev)}
                         style={{ background: '#f39c1218', borderBottom: '1px solid #f39c1244', padding: '5px 16px', fontSize: 12, color: '#f39c12', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>⚠</span>
                        <span>Прихована подія поза сіткою:</span>
                        <b>{ev.title}</b>
                        <span style={{ color: '#888' }}>— {format(new Date(ev.start_time), 'EEE d MMM · HH:mm', { locale: uk })}</span>
                        <span style={{ marginLeft: 'auto', color: '#666', fontSize: 11 }}>натисніть щоб відкрити / видалити</span>
                    </div>
                ))}

                {/* Day headers */}
                <div style={{ display: 'flex', borderBottom: '1px solid #2e2e2e', background: '#1a1a1a', paddingLeft: GUTTER_W }}>
                    {days.map(day => (
                        <div key={day.toISOString()} style={{
                            flex: 1, textAlign: 'center', padding: '8px 0', borderLeft: '1px solid #2e2e2e',
                            color: isSameDay(day, new Date()) ? '#5865f2' : '#888',
                            fontWeight: isSameDay(day, new Date()) ? 700 : 400, fontSize: 13,
                        }}>
                            <div style={{ fontSize: 11 }}>{format(day, 'EEE', { locale: uk }).toUpperCase()}</div>
                            <div style={{ fontSize: 20, fontWeight: 700 }}>{format(day, 'd')}</div>
                        </div>
                    ))}
                </div>

                {/* Grid */}
                <div
                    ref={gridRef}
                    style={{ flex: 1, minHeight: 0, overflowY: 'scroll', scrollbarGutter: 'stable', display: 'flex', cursor: activeEv ? 'grabbing' : 'default', position: 'relative' }}
                >
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,15,15,0.6)', backdropFilter: 'blur(2px)' }}>
                            <PulseLoader color="#5865f2" size={10} speedMultiplier={0.8} />
                        </div>
                    )}

                    {/* Time gutter */}
                    <div style={{ width: GUTTER_W, flexShrink: 0, position: 'relative', background: '#0f0f0f' }}>
                        {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                            <div key={i} style={{ position: 'absolute', top: i * HOUR_HEIGHT - 8, right: 6, fontSize: 10, color: '#444' }}>
                                {String(START_HOUR + i).padStart(2, '0')}:00
                            </div>
                        ))}
                    </div>

                    {/* Day columns */}
                    {days.map((day, colIdx) => (
                        <div
                            key={day.toISOString()}
                            style={{ flex: 1, position: 'relative', height: TOTAL_HOURS * HOUR_HEIGHT, borderLeft: '1px solid #2e2e2e' }}
                            onClick={e => handleColClick(e, day)}
                        >
                            {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                                <div key={i} style={{ position: 'absolute', top: i * HOUR_HEIGHT, left: 0, right: 0, borderTop: '1px solid #191919', pointerEvents: 'none' }} />
                            ))}

                            {events.filter(ev => isSameDay(new Date(ev.start_time), day)).map(ev => (
                                <EventBlock
                                    key={ev.id}
                                    event={ev}
                                    top={timeToY(ev.start_time)}
                                    height={durationH(ev)}
                                    dayIdx={colIdx}
                                    onClick={() => { if (!justDragged.current) setDetailEvent(ev); }}
                                    onDone={() => { if (!justDragged.current) setDoneEvent(ev); }}
                                />
                            ))}
                        </div>
                    ))}
                </div>

                {/* Drag overlay — shown while dragging */}
                <DragOverlay dropAnimation={null}>
                    {activeEv ? (
                        <div style={{
                            background: COLOR_BG[activeEv.color_type] ?? COLOR_BG.green,
                            border: COLOR_BORDER[activeEv.color_type] ?? 'none',
                            borderRadius: 5, padding: '4px 8px',
                            color: '#fff', fontSize: 12, fontWeight: 600,
                            opacity: 0.92,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                            transform: 'scale(1.03) rotate(1deg)',
                            cursor: 'grabbing',
                            whiteSpace: 'nowrap',
                            display: 'flex', flexDirection: 'column', gap: 2,
                        }}>
                            <span>{activeEv.title}</span>
                            {dragTime && (
                                <span style={{ fontSize: 14, fontWeight: 700, opacity: 0.95, letterSpacing: 0.5 }}>
                                    {dragTime}
                                </span>
                            )}
                        </div>
                    ) : null}
                </DragOverlay>
            </div>

            {showCreate && <CreateEventModal show onHide={() => setShowCreate(false)} initialSlot={createSlot} onCreated={load} />}
            {detailEvent && (
                <EventDetailModal show event={detailEvent} onHide={() => setDetailEvent(null)}
                                  onDone={ev => { setDetailEvent(null); setDoneEvent(ev); }} onRefresh={load} />
            )}
            {doneEvent && <DoneEventModal show event={doneEvent} onHide={() => setDoneEvent(null)} onSaved={() => { setDoneEvent(null); load(); }} />}
        </DndContext>
    );
}

const navBtn = { background: '#242424', border: '1px solid #2e2e2e', color: '#e8e8e8', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 };
